import { Icon } from '@acme/assets';
import { createTheme, Dropdown, DropdownItem, TextInput } from 'flowbite-react';
import { ReactNode } from 'react';

export interface DropdownSelectOption {
  value: string;
  label: string | ReactNode;
  displayLabel?: string;
}

export interface DropdownSelectProps {
  options: DropdownSelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  renderOption?: (option: DropdownSelectOption) => ReactNode;
  clearable?: boolean;
}

const customDropdownTheme = createTheme({
  content: 'p-2 cursor-pointer',
  floating: {
    base: 'z-10 rounded-lg bg-white shadow-sm bg-red-500',
    content: 'p-2 gap-1',
    target: 'w-full',
    item: {
      base: 'flex w-full cursor-pointer items-center justify-start text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:text-gray-200 dark:hover:bg-gray-600 dark:hover:text-white dark:focus:bg-gray-600 dark:focus:text-white',
      icon: 'mr-2 h-4 w-4',
    },
  },
});

const customTextInputTheme = {
  field: {
    input: {
      base: 'cursor-pointer',
    },
  },
};

export const DropdownSelect: React.FC<DropdownSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Выберите опцию',
  disabled = false,
  className = '',
  renderOption,
  clearable = false,
}) => {
  const selectedOption = options.find((opt) => opt.value === value);

  const displayValue = selectedOption
    ? selectedOption.displayLabel ||
      (typeof selectedOption.label === 'string' ? selectedOption.label : '')
    : '';

  const handleOptionClick = (optionValue: string) => {
    if (clearable && value && optionValue === value) {
      onChange?.('');
      return;
    }
    onChange?.(optionValue);
  };

  const defaultRenderOption = (option: DropdownSelectOption) => (
    <div>{option.label}</div>
  );

  return (
    <div className={`relative  cursor-pointer ${className}`}>
      <Dropdown
        theme={customDropdownTheme}
        label=""
        dismissOnClick={true}
        renderTrigger={() => (
          <div className="relative group">
            <TextInput
              type="text"
              placeholder={placeholder}
              value={displayValue}
              readOnly
              disabled={disabled}
              theme={customTextInputTheme}
              className="cursor-pointer"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {clearable && !!value ? (
                <button
                  type="button"
                  className="hidden group-aria-expanded:flex items-center justify-center text-gray-500 hover:text-gray-700"
                  aria-label="Сбросить"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange?.('');
                  }}
                >
                  <Icon name="x-outline" size={12} />
                </button>
              ) : null}

              <span className="flex items-center justify-center pointer-events-none group-aria-expanded:rotate-180 transition-transform">
                <Icon
                  name="chevron-down"
                  size={16}
                  className={
                    clearable && !!value ? 'group-aria-expanded:hidden' : ''
                  }
                />
              </span>
            </div>
          </div>
        )}
      >
        <div className="flex flex-col gap-1 w-full max-h-[300px] overflow-y-auto">
          {options.map((option) => (
            <DropdownItem
              key={option.value}
              className="w-full px-3 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-100 cursor-pointer rounded-lg transition-colors"
              onClick={() => handleOptionClick(option.value)}
            >
              {renderOption
                ? renderOption(option)
                : defaultRenderOption(option)}
            </DropdownItem>
          ))}
        </div>
      </Dropdown>
    </div>
  );
};
